/**
 * Viewport-clamped placement for a fixed panel hanging off an anchor rect.
 *
 * Extracted from `usePopoverPosition` so the guided-tour bubble clamps with exactly the same
 * arithmetic as every portalled picker, rather than re-deriving it.
 */

export const VIEWPORT_MARGIN = 8;

export interface AnchorRect {
  top: number;
  bottom: number;
  left: number;
  right: number;
  width: number;
  height: number;
}

export interface PanelPlacementInput {
  anchor: AnchorRect;
  panelWidth: number;
  panelHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  /** Distance between the anchor and the panel. */
  gap?: number;
}

export interface PanelPosition {
  top: number;
  left: number;
}

/**
 * Below the anchor when it fits or is the roomier side, otherwise above. Always clamped so the
 * panel stays on screen even when neither side has room.
 */
export const computeAnchoredPosition = ({
  anchor,
  panelWidth,
  panelHeight,
  viewportWidth,
  viewportHeight,
  gap = 4,
}: PanelPlacementInput): PanelPosition => {
  const spaceBelow = viewportHeight - anchor.bottom - VIEWPORT_MARGIN;
  const spaceAbove = anchor.top - VIEWPORT_MARGIN;

  let top: number;
  if (spaceBelow >= panelHeight || spaceBelow >= spaceAbove) {
    // Below the trigger, but clamped so the bottom edge stays on screen even when neither
    // side has room. A picker whose confirm button is off the viewport is unusable.
    top = Math.min(
      anchor.bottom + gap,
      Math.max(VIEWPORT_MARGIN, viewportHeight - panelHeight - VIEWPORT_MARGIN)
    );
  } else {
    // Flip above when that is genuinely the roomier side.
    top = Math.max(VIEWPORT_MARGIN, anchor.top - panelHeight - gap);
  }

  const left = Math.min(
    Math.max(VIEWPORT_MARGIN, anchor.left),
    Math.max(VIEWPORT_MARGIN, viewportWidth - panelWidth - VIEWPORT_MARGIN)
  );

  return { top, left };
};
